<!-- eslint-disable tailwindcss/no-custom-classname -->
<template>
	<div
		class="relative size-full"
		:class="{
			'aspect-3/2': cropImage,
			'overflow-hidden': blok.enable_background_animation,
		}"
	>
		<template v-if="blok.product_links && blok.product_links.length > 0">
			<template v-for="marker in blok.product_links" :key="marker.id">
				<v-storyblok-product-image-lift-marker
					v-if="marker.product?.source"
					:marker="marker"
					:products="products"
					class="absolute top-(--marker-position-y) left-(--marker-position-x)"
					:style="{
						'--marker-position-x': `${marker.position_x}%`,
						'--marker-position-y': `${marker.position_y}%`,
					}"
				/>
			</template>
		</template>

		<!-- Single image -->
		<!-- overflow-hidden clips the image scaled beyond bounds by the ken burns animation -->
		<template v-if="blok.image && !blok.enable_slideshow">
			<!-- Product image with the links to the room. -->
			<nuxt-link
				v-if="blok.link_to_room.cached_url"
				:to="formatSbUrl(blok.link_to_room)"
				:aria-label="blok.title"
				:title="blok.image.alt"
				@click="addPromotionClick(promo)"
			>
				<nuxt-picture
					:src="blok.image.filename"
					class="aspect-3/2"
					width="760"
					height="507"
					provider="storyblok"
					fit="in"
					sizes="sm:100vw md:768px lg:504px xl:632px 2xl:760px"
					:img-attrs="{
						// eslint-disable-next-line max-len
						class:
							'object-cover w-full h-full' +
							(blok.enable_background_animation
								? ' motion-safe:animate-ken-burns'
								: ''),
					}"
					:alt="blok.image.alt ?? blok.title"
				/>
			</nuxt-link>

			<!-- Product image without the links to the room. -->
			<div
				v-else
				:class="{'overflow-hidden': blok.enable_background_animation}"
			>
				<nuxt-picture
					:src="blok.image.filename"
					class="aspect-3/2"
					width="760"
					height="507"
					provider="storyblok"
					fit="in"
					sizes="sm:100vw md:768px lg:504px xl:632px 2xl:760px"
					:img-attrs="{
						// eslint-disable-next-line max-len
						class:
							'object-cover w-full h-full' +
							(blok.enable_background_animation
								? ' motion-safe:animate-ken-burns'
								: ''),
					}"
					:alt="blok.image.alt ?? blok.title"
				/>
			</div>
		</template>

		<!-- Carousel -->
		<template v-if="blok.images && blok.enable_slideshow">
			<div class="relative">
				<v-auto-scroller :items="blok.images">
					<template #item="{filename, alt, title}">
						<nuxt-link
							v-if="blok.link_to_room.cached_url.length > 0"
							:to="formatSbUrl(blok.link_to_room)"
							:aria-label="blok.title"
							:title="blok.image.alt"
							@click="addPromotionClick(promo)"
						>
							<nuxt-picture
								:src="filename"
								class="aspect-3/2"
								width="760"
								height="507"
								provider="storyblok"
								fit="in"
								sizes="sm:100vw md:768px lg:504px xl:632px 2xl:760px"
								:img-attrs="{
									// eslint-disable-next-line max-len
									class:
										'object-cover size-full' +
										(blok.enable_background_animation
											? ' motion-safe:animate-ken-burns'
											: ''),
								}"
								:alt="alt ?? title"
							/>
						</nuxt-link>

						<template v-else>
							<nuxt-picture
								:src="filename"
								class="aspect-3/2"
								width="760"
								height="507"
								provider="storyblok"
								fit="in"
								sizes="sm:100vw md:768px lg:504px xl:632px 2xl:760px"
								:img-attrs="{
									// eslint-disable-next-line max-len
									class:
										'object-cover size-full' +
										(blok.enable_background_animation
											? ' motion-safe:animate-ken-burns'
											: ''),
								}"
								:alt="alt ?? title"
							/>
						</template>
					</template>
				</v-auto-scroller>
			</div>
		</template>

		<!-- Text content. -->
		<div
			v-if="!!blok.title || !!blok.cta_text"
			class="pointer-events-none absolute top-1/2 left-1/2 z-9 w-full -translate-1/2 space-y-md p-xl text-center"
		>
			<div v-if="blok.title">
				<p
					class="text-5xl font-bold text-(--text-color) md:text-8xl lg:text-7xl xl:text-9xl"
					:class="{
						'custom-shadow': blok.enable_text_shadow,
					}"
					:style="{
						'--text-color': titleTextColor,
					}"
				>
					{{ blok.title }}
				</p>
			</div>

			<v-button v-if="blok.cta_text" :theme="ctaButtonColor" size="base">
				{{ blok.cta_text }}
			</v-button>
		</div>
	</div>
</template>

<script setup lang="ts">
import type {BlokComponent, ButtonThemes} from "@pkg/shared/types";
import {usePromotion} from "@pkg/shared/composables/use-promotion";
import formatSbUrl from "@pkg/shared/utils/format-sb-url";
import type {ViewMarkerProductsQuery} from "@pkg/shared/graphql";

const properties = defineProps<{
	blok: Extract<BlokComponent, {component: "sb-product-image-lift"}>;
	id?: string | number;
	name?: string | null;
	creative?: string | null;
	page?: string;
	type?: string;
	products?: ViewMarkerProductsQuery["products"]["edges"][0]["node"][];
}>();

const route = useRoute();
const {addPromotionClick} = usePromotion();

function convertString(string: string) {
	const words = string.split("-");
	const convertedString = words
		.map((word) => word.charAt(0).toUpperCase() + word.slice(1))
		.join(" ");
	return convertedString;
}

/** Title text color. */
const titleTextColor = computed<string>(
	() => properties.blok.text_color.color ?? "#FFF",
);

/** CTA button color property.
 *  We can cast the color from Storyblok to button component theme.
 *  If the color is not allowed, we will default to white.
 */
const ctaButtonColor = computed<ButtonThemes>(() => {
	const allowedColors: ButtonThemes[] = ["black", "white"];
	return allowedColors.includes(
		properties.blok.cta_background_color as ButtonThemes,
	)
		? (properties.blok.cta_background_color as ButtonThemes)
		: "white";
});

const promo = computed(() => ({
	creative_name: convertString(properties.blok.component),
	creative_slot: properties.blok.component, // component name

	promotion_id: properties.blok._uid,
	promotion_name: properties.blok.title,
	source_url: route.fullPath,
	target_url: properties.blok.link_to_room.cached_url,
}));

const cropImage = computed(() => properties.blok.use_autocrop ?? false);
</script>

<style scoped>
.custom-shadow {
	text-shadow: 2.5px 4px 3px rgba(0, 0, 0, 0.7);
}
</style>
